import { activeIcon } from "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import "../../../components/avatar/avatar";
import { Checkbox } from "../../../elements/checkbox";
import "../../../elements/employee-list";
import type { EmployeeList } from "../../../elements/employee-list";
import "../../../elements/scroller";
import "../../../elements/send-message-dialog";
import type { SendMessageDialog } from "../../../elements/send-message-dialog";
import { app } from "../../../init";
import { canAccess, menuGroups, menuItemState } from "../../../lib/route-tree";
import { singleton } from "../../../lib/singleton";
import { Routing, routeProperty } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { mixins, shared } from "../../../styles";
import { type EmployeesAllPage, employeesAllPages, employeesAllRoute } from "../../app-routes";
import { routeAccess } from "../../route-access";
import "../components/contract-form";
import { getEmployeeRangeSlot, getRememberedEmployeeRange, rememberEmployeeRange } from "../shared-range-memory";
import "./employees-absences-all";
import "./employees-advances-all";
import "./employees-availabilities-all";
import "./employees-bonuses-balance-all";
import "./employees-contracts-all";
import "./employees-documents-all";
import "./employees-ledgers-all-month";
import "./employees-ledgers-all-year";
import "./employees-permissions-all";
import "./employees-positions-all";
import "./employees-profile-all";
import "./employees-time-all";
import "./employees-time-balance-all";
import "./employees-vacation-balance-all";

@customElement("ptc-employees-all")
export class EmployeesAll extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/all(?:\/(?<page>[^/]+))?/;

    get helpPage() {
        switch (this._page) {
            case "core-data":
                return "/handbuch/mitarbeiter/stammdaten";
            case "ledgers-month":
            case "ledgers-year":
                return "/handbuch/mitarbeiter/konten";
            case "time":
                return "/handbuch/mitarbeiter/arbeitszeiten";
            case "time-balance":
                return "/handbuch/mitarbeiter/arbeitszeitkonto";
            case "vacation-balance":
                return "/handbuch/mitarbeiter/urlaubskonto";
            case "bonuses-balance":
                return "/handbuch/mitarbeiter/zuschlagskonto";
            case "absences":
                return "/handbuch/mitarbeiter/abwesenheiten";
            case "documents":
                return "/handbuch/mitarbeiter/dokumente";
            case "availabilities":
                return "/handbuch/mitarbeiter/verfuegbarkeiten";
            case "advances":
                return "/handbuch/mitarbeiter/vorschuesse";
            case "positions":
                return "/handbuch/mitarbeiter/positionen";
            case "permissions":
                return "/handbuch/mitarbeiter/zugaenge-rechte";
            case "contracts":
                return "/handbuch/mitarbeiter/vertrag";
            default:
                return "/handbuch/mitarbeiter";
        }
    }

    @routeProperty({ arg: "page" })
    private _page: string;

    @state()
    private _loading: boolean = false;

    @state()
    private _selectingEmployee: boolean = false;

    @query("ptc-employee-list")
    private _employeeList: EmployeeList;

    @singleton("ptc-send-message-dialog")
    private _sendMessageDialog: SendMessageDialog;

    async handleRoute() {
        if (this._page === "ledgers") {
            this.go(`employees/all/ledgers-month`, undefined, true);
            return;
        }

        if (!this._isPage(this._page) || !this._canAccessPage(this._page)) {
            const page = ["core-data" as const, ...employeesAllPages].find((page) => this._canAccessPage(page));
            this.redirect(page ? `employees/all/${page}` : "");
            return;
        }
    }

    private _isPage(page: string): page is EmployeesAllPage {
        return (employeesAllPages as string[]).includes(page);
    }

    private _canAccessPage(page: EmployeesAllPage) {
        return canAccess(employeesAllRoute(page), routeAccess());
    }

    private _renderButton(page: EmployeesAllPage) {
        const route = employeesAllRoute(page);
        const active = this._page === page || (page === "ledgers-month" && this._page === "ledgers-year");
        return html`
            <button
                ?active=${active}
                @click=${() => this._goToPage(page)}
                ?disabled=${menuItemState(route, routeAccess()) === "disabled"}
            >
                ${route.icon ? html`<ptc-ui-icon name=${activeIcon(route.icon, active)}></ptc-ui-icon>` : nothing}
                ${route.label}
            </button>
        `;
    }

    private _renderMenuGroups() {
        const access = routeAccess();
        const visible = employeesAllPages.filter((page) => menuItemState(employeesAllRoute(page), access) !== "hidden");
        return menuGroups(visible, employeesAllRoute).map(
            (pages) => html`
                <div class="horizontally-margined vertically-padded vertical tabs border-bottom">
                    ${pages.map((page) => this._renderButton(page))}
                </div>
            `
        );
    }

    private async _startSelectEmployee(e: MouseEvent) {
        e.stopPropagation();
        this._selectingEmployee = true;
        await this.updateComplete;
        this._employeeList.clearFilter();
        this._employeeList.focus();
    }

    private _employeeSelected(e: CustomEvent) {
        const currentSlot = getEmployeeRangeSlot("all", this._page);
        if (currentSlot) {
            const { from, to, date } = this.router.params;
            rememberEmployeeRange(currentSlot, { from, to, date });
        }

        // Carry the shared month into the employee's monthly pages (Arbeitszeiten,
        // Vorschüsse). Their account/absence/availability pages keep their own
        // history default instead, so we deliberately pass no range there.
        const targetSlot = getEmployeeRangeSlot("single", this._page);
        const remembered = targetSlot === "month" ? getRememberedEmployeeRange("month") : undefined;
        this.go(`employees/${e.detail.employee.id}/${this._page}`, remembered);
        this._selectingEmployee = false;
    }

    private _goToPage(page: string, params?: { [prop: string]: number | string | Date }) {
        const currentSlot = getEmployeeRangeSlot("all", this._page);
        if (currentSlot) {
            const { from, to, date } = this.router.params;
            rememberEmployeeRange(currentSlot, { from, to, date });
        }

        const targetSlot = getEmployeeRangeSlot("all", page);
        const remembered = targetSlot ? getRememberedEmployeeRange(targetSlot) : undefined;
        this.go(`employees/all/${page}`, { ...remembered, ...params });
    }

    private async _sendMessage() {
        const filteredDepartments =
            app.employeeFilters.filters
                .filter((f) => f.type === "department")
                .map(({ value }) => app.accessibleDepartments.find((d) => d.id === value)!) || [];
        const isFiltered = filteredDepartments.length !== app.accessibleDepartments.length;
        await this._sendMessageDialog.show({
            all: !isFiltered,
            departments: isFiltered ? filteredDepartments : undefined,
        });
    }

    private _documentClickHandler = () => (this._selectingEmployee = false);

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener("click", this._documentClickHandler);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener("click", this._documentClickHandler);
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            :host {
                display: flex;
            }

            .sub-menu {
                width: 15em;
                padding: 0;
                position: relative;
            }

            .employee-list {
                ${mixins.fullbleed()};
                background: var(--color-bg);
                z-index: 1;
            }

            .employee-avatar {
                font-size: var(--font-size-enormous);
                align-self: center;
                border: solid 0.15em;
                border-radius: 100%;
                text-align: center;
                background: var(--shade-1);
                color: var(--shade-5);
                width: 2.5em;
                height: 2.5em;
                line-height: 2.5em;
                margin: 0.5em;
                margin-top: 0;
            }

            .employee-name {
                font-size: var(--font-size-medium);
                align-self: center;
                margin-bottom: 0.5em;
                padding: 0.3em;
                padding-left: 0.5em;
            }

            .sub-view {
                flex: 1;
                min-width: 0;
                position: relative;
                border-right: solid 1px var(--shade-1);
            }

            .sub-view > * {
                ${mixins.fullbleed()};
            }

            @media print {
                .sub-view > * {
                    position: static;
                }

                .sub-view {
                    border: none;
                }
            }
        `,
    ];

    private _routeMap = new Map([
        ["core-data", () => html`<ptc-employees-profile-all class="fullbleed"></ptc-employees-profile-all>`],
        ["time", () => html`<ptc-employees-time-all class="fullbleed"></ptc-employees-time-all>`],
        [
            "ledgers-month",
            () => html`<ptc-employees-ledgers-all-month class="fullbleed"></ptc-employees-ledgers-all-month>`,
        ],
        [
            "time-balance",
            () => html`<ptc-employees-time-balance-all class="fullbleed"></ptc-employees-time-balance-all>`,
        ],
        [
            "vacation-balance",
            () => html`<ptc-employees-vacation-balance-all class="fullbleed"></ptc-employees-vacation-balance-all>`,
        ],
        [
            "bonuses-balance",
            () => html`<ptc-employees-bonuses-balance-all class="fullbleed"></ptc-employees-bonuses-balance-all>`,
        ],
        [
            "ledgers-year",
            () => html`<ptc-employees-ledgers-all-year class="fullbleed"></ptc-employees-ledgers-all-year>`,
        ],
        ["advances", () => html`<ptc-employees-advances-all class="fullbleed"></ptc-employees-advances-all>`],
        ["positions", () => html`<ptc-employees-positions-all class="fullbleed"></ptc-employees-positions-all>`],
        ["permissions", () => html`<ptc-employees-permissions-all class="fullbleed"></ptc-employees-permissions-all>`],
        ["absences", () => html`<ptc-employees-absences-all class="fullbleed"></ptc-employees-absences-all>`],
        ["documents", () => html`<ptc-employees-documents-all class="fullbleed"></ptc-employees-documents-all>`],
        [
            "availabilities",
            () => html`<ptc-employees-availabilities-all class="fullbleed"></ptc-employees-availabilities-all>`,
        ],
        ["contracts", () => html`<ptc-employees-contracts-all class="fullbleed"></ptc-employees-contracts-all>`],
    ]);

    private _renderRoute(route: string) {
        const renderer = this._routeMap.get(route);
        return renderer ? renderer() : nothing;
    }

    private _renderSubMenu() {
        return html`
            <ptc-employee-list
                @change=${this._employeeSelected}
                class="employee-list"
                ?hidden=${!this._selectingEmployee}
            >
            </ptc-employee-list>

            <div class="horizontally-margined vertically-padded vertical text-centering layout border-bottom">
                <div class="horizontal end-justifying layout">
                    <button
                        class="transparent skinny"
                        @click=${this._sendMessage}
                        title="Nachricht Versenden"
                        ?disabled=${!app.hasPermission("manage.employees.messages")}
                    >
                        <ptc-ui-icon name="icon.action.send-message"></ptc-ui-icon>
                    </button>

                    <button
                        class="transparent skinny"
                        @click=${() =>
                            this.go("exports/employee-data-pdf", {
                                "entity-filters": JSON.stringify(app.employeeFilters.filters),
                            })}
                        title="Datenblätter Drucken"
                        ?disabled=${!app.hasPermission("manage.employees.contract")}
                    >
                        <ptc-ui-icon name="icon.object.master-data"></ptc-ui-icon>
                    </button>
                </div>

                <ptc-ui-icon name="icon.object.employee-group" class="employee-avatar"></ptc-ui-icon>

                <button
                    class="center-aligning center-justifying horizontal layout employee-name transparent"
                    @click=${this._startSelectEmployee}
                >
                    <div>Alle Mitarbeiter</div>
                    <ptc-ui-icon name="icon.navigation.dropdown"></ptc-ui-icon>
                </button>
            </div>

            <ptc-scroller class="stretch">
                ${this._renderMenuGroups()}
                <div class="horizontally-margined vertically-padded vertical tabs border-bottom">
                    <button
                        data-testid="employee-new-button"
                        @click=${() => this.dispatchEvent(new CustomEvent("newemployee"))}
                        ?disabled=${!app.hasPermission("manage.employees.create")}
                    >
                        <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                        Neuer Mitarbeiter
                    </button>
                </div>
            </ptc-scroller>
        `;
    }

    render() {
        return html`
            <div class="sub-menu noprint vertical layout">${this._renderSubMenu()}</div>
            <div class="sub-view">
                ${this._renderRoute(this._page)}

                <div
                    class="fullbleed center-aligning center-justifying vertical layout scrim"
                    ?hidden=${!this._loading}
                >
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            </div>
        `;
    }
}
